import {
  ObservationSchema,
} from '@ynomia/core/dist/blueprint';
import { createSelector } from 'reselect';
import {
  getSelectedAssetType,
  getSummariesConfig,
} from '.';
import { LocationSlot, LocationSummaryConfig } from '../config/types';
import { DEFAULT_STATUS_COLOR, DEFAULT_SUMMARIES } from '../config/constants';

export const getObservationTypes = createSelector(
  [getSelectedAssetType],
  (selectedAssetType) => {
    const observationTypes = selectedAssetType?.observations || [];
    return observationTypes;
  },
);

export const getObservationTypesKeyedById = createSelector(
  [getSelectedAssetType],
  (selectedAssetType) => {
    const observationTypes = selectedAssetType?.observations || [];
    const observationTypesKeyedById = new Map();

    observationTypes.forEach((type) => {
      observationTypesKeyedById.set(type.id, type);
    });

    return observationTypesKeyedById as Map<string, ObservationSchema>;
  },
);

export const getSelectedAssetTypeFields = createSelector(
  [getSelectedAssetType],
  selectedAssetType => selectedAssetType?.fields || [],
);

export const getSelectedAssetTypeStatuses = createSelector(
  [getSelectedAssetType],
  (selectedAssetType) => {
    const statuses = selectedAssetType?.statuses || [];
    return statuses;
  },
);

export const getColorSchemeForStatus = createSelector(
  [getSelectedAssetType],
  (selectedAssetType): Record<string, string> => {
    const assetStatuses = selectedAssetType?.statuses || [];
    return assetStatuses.reduce((acc, { label, color }) => ({
      ...acc,
      [label]: color || DEFAULT_STATUS_COLOR,
    }), {});
  },
);

export const getSelectedSummariesConfig = createSelector(
  [getSelectedAssetType, getSummariesConfig],
  (selectedAssetType, summariesConfig) => summariesConfig[selectedAssetType.id]
  || DEFAULT_SUMMARIES,
);

export const getPlanIDsForSelectedAssetType = createSelector(
  [getSelectedAssetType],
  selectedAssetType => selectedAssetType.plans?.map(p => p.id) ?? [],
);

/**
 * Resolves the location summary's slot config into the list of slots an
 * asset is located through. Empty when the summary locates assets by their
 * own layer. locationSlotIds wins over locationSlotId; the latter is kept so
 * existing single-slot configs keep working. Slots not defined on the
 * selected asset type are dropped.
 */
export const getLocationSummarySlotConfig = createSelector(
  [getSelectedSummariesConfig, getSelectedAssetType],
  (summariesConfig, selectedAssetType): {
    slots: Array<LocationSlot>,
    fieldFilter: { [key: string]: Array<string> } | undefined,
  } => {
    const locationSummary = summariesConfig.find(
      s => s.type === 'location_summary',
    ) as LocationSummaryConfig | undefined;
    const fieldFilter = locationSummary?.fieldFilter;
    const configuredIds = locationSummary?.locationSlotIds?.length
      ? locationSummary.locationSlotIds
      : [locationSummary?.locationSlotId].filter((id): id is string => !!id);
    const slots = configuredIds.flatMap((id): Array<LocationSlot> => {
      const slotDef = selectedAssetType?.slots?.find(s => s.id === id);
      if (!slotDef) return [];
      return [{
        id,
        direction: slotDef.relationship === 'has_many' ? 'children' : 'parents',
      }];
    });
    return { slots, fieldFilter };
  },
);
